<template>
  <div class="cover-container w-100 mx-auto" id="main_page">
    <!-- MapLibre 地圖容器 -->
    <div ref="mapContainer" class="maplibre-container"></div>

    <!-- 縮放按鈕 -->
    <div class="zoom-controls">
      <button class="zoom-btn" @click="zoomIn" :disabled="currentZoom >= 19">+</button>
      <button class="zoom-btn" @click="zoomOut" :disabled="currentZoom <= 18">−</button>
    </div>

    <!-- 測試座標／真實 GPS 切換：僅開發者模式顯示，上線前 DEV_MODE 必須是 false -->
    <button class="gps-toggle-btn" v-if="devMode" @click="toggleGpsMode">
      {{ testGps ? "測試座標" : "真實 GPS" }}
    </button>

    <!-- 定位狀態提示 -->
    <div class="location-status" v-if="locationStatus">{{ locationStatus }}</div>

    <TopNavigation
      :showIndexBtn="isShowIndexBtn"
      :showMapBtn="isShowMapBtn"
      :showDataBtn="isShowDataBtn"
      @back-to-index="onBackToIndex"
    />
    <div class="hint-lightbox" v-if="isShowHint">
      <div class="hint-content">
        <div
          class="hint-title"
          :class="{ en: currentLanguage === 'en' || currentLanguage === 'bn' }"
        >
          {{ languageTexts[currentLanguage].hintTitle }}
        </div>
        <div class="hint-box">
          <div class="topic">
            <p v-html="languageTexts[currentLanguage].hintText"></p>
          </div>
        </div>
        <div class="hint-button" @click="closeHintBox">
          {{ languageTexts[currentLanguage].hintButton }}
        </div>
      </div>
    </div>

    <!-- 景點 Lightbox -->
    <transition name="spot-slide">
      <div class="spot-overlay" v-if="selectedSpot">
        <div class="spot-card">
          <div class="spot-photo-area">
            <template v-if="selectedSpot.photos && selectedSpot.photos.length">
              <div
                class="spot-photo-track"
                :style="{ transform: `translateX(-${spotPhotoIndex * 100}%)` }"
              >
                <img
                  v-for="(photo, idx) in selectedSpot.photos"
                  :key="idx"
                  :src="photo"
                  class="spot-photo"
                  alt=""
                />
              </div>
              <div class="spot-photo-dots" v-if="selectedSpot.photos.length > 1">
                <span
                  v-for="(_, idx) in selectedSpot.photos"
                  :key="idx"
                  class="spot-photo-dot"
                  :class="{ active: idx === spotPhotoIndex }"
                ></span>
              </div>
            </template>
            <div v-else class="spot-photo-placeholder"></div>
            <button class="spot-close-btn" @click="closeLightbox">✕</button>
          </div>
          <div class="spot-info">
            <div class="spot-info-left">
              <div class="spot-name-row">
                <span class="spot-name">{{ selectedSpot.name[currentLanguage] }}</span>
                <img
                  v-if="getSpotIcon(selectedSpot.id)"
                  :src="getSpotIcon(selectedSpot.id)"
                  class="spot-name-icon"
                  alt=""
                />
              </div>
              <div class="spot-scientific">
                {{ languageTexts[currentLanguage].scientificLabel }}：{{
                  selectedSpot.scientificName
                }}
              </div>
            </div>
            <div class="spot-btn-group">
              <button
                class="spot-ar-btn"
                :disabled="isSpotOutOfRange"
                @click="goToAR(selectedSpot.id)"
              >
                {{ languageTexts[currentLanguage].arButton }} →
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Cookies from "js-cookie";
import { getBasePath } from "@/utils/urlBase";
import { hasSeenMapHint, markMapHintSeen } from "@/utils/hintFlag";
import TopNavigation from "@/components/TopNavigation.vue";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import customMapImg from "@/assets/images/customMap.jpg";
import mapManImg from "@/assets/images/map_man.gif";
import mapDirectionImg from "@/assets/images/map_direction.png";

// 切換為 false 關閉開發者模式（測試座標切換鈕等）。上線前必須是 false。
const DEV_MODE = false;

const MIN_ZOOM = 16;
const MAX_ZOOM = 19;
const ZOOM_STEP = 0.5;

// 使用者距離目前選中的景點超過這個距離（公尺）時，AR 體驗按鈕停用
const AR_TRIGGER_DISTANCE = 50;

// 固定 GPS 測試座標（供「測試座標／真實 GPS」切換按鈕使用）
const TEST_LNG = 121.117829;
const TEST_LAT = 22.792217;

const BOUNDS = {
  west: 121.1122944352051085,
  east: 121.1229642590001922,
  south: 22.7902068479598781,
  north: 22.7985221229500097,
}; // 台東正式

// ── 各景點的非座標資料（名稱、學名、照片）──────────────────────────
// photos: 使用 require() 讓 webpack 正確打包。每個景點可能有多張照片：
// image_<id>.png 為第一張，image_<id>_2.png、_3.png... 為後續照片，檔案不存在時自動略過。
function loadSpotPhotos(id) {
  const photos = [];
  try {
    photos.push(require(`@/assets/images/image_${id}.jpg`));
  } catch {
    // 第一張照片不存在
  }
  for (let i = 2; i <= 9; i++) {
    try {
      photos.push(require(`@/assets/images/image_${id}_${i}.jpg`));
    } catch {
      break; // 遇到第一個不存在的編號即停止，避免略過中間空缺的編號
    }
  }
  return photos;
}

const SPOT_META = {
  VitexNigra: {
    img: require("@/assets/images/circle_VitexNigra.png"),
    name: { zh: "黃荊", en: "Chinese Chaste Tree", bn: "ニンジンボク" },
    scientificName: "Vitex negundo",
    photos: loadSpotPhotos("VitexNigra"),
  },
  Mango: {
    img: require("@/assets/images/circle_Mango.png"),
    name: { zh: "芒果", en: "Mango", bn: "マンゴー" },
    scientificName: "Mangifera indica",
    photos: loadSpotPhotos("Mango"),
  },
  PaperMulberry: {
    img: require("@/assets/images/circle_PaperMulberry.png"),
    name: { zh: "構樹", en: "Paper Mulberry", bn: "カジノキ" },
    scientificName: "Broussonetia papyrifera",
    photos: loadSpotPhotos("PaperMulberry"),
  },
  SpinyBamboo: {
    img: require("@/assets/images/circle_SpinyBamboo.png"),
    name: { zh: "刺竹", en: "Spiny Bamboo", bn: "ショクチク" },
    scientificName: "Bambusa stenostachya",
    photos: loadSpotPhotos("SpinyBamboo"),
  },
  Chinaberry: {
    img: require("@/assets/images/circle_Chinaberry.png"),
    name: { zh: "苦楝", en: "Chinaberry", bn: "センダン" },
    scientificName: "Melia azedarach",
    photos: loadSpotPhotos("Chinaberry"),
  },
  Areca: {
    img: require("@/assets/images/circle_Areca.png"),
    name: { zh: "檳榔", en: "Areca Palm", bn: "ビンロウ" },
    scientificName: "Areca catechu",
    photos: loadSpotPhotos("Areca"),
  },
  Parasolleaftree: {
    img: require("@/assets/images/circle_Parasolleaftree.png"),
    name: { zh: "血桐", en: "Parasol Leaf Tree", bn: "マカランガ" },
    scientificName: "Macaranga tanarius",
    photos: loadSpotPhotos("Parasolleaftree"),
  },
  TaiwanFlamegold: {
    img: require("@/assets/images/circle_TaiwanFlamegold.png"),
    name: { zh: "台灣欒樹", en: "Taiwan Flamegold", bn: "タイワンモクゲンジ" },
    scientificName: "Koelreuteria elegans",
    photos: loadSpotPhotos("TaiwanFlamegold"),
  },
  Barringtoniaasiatica: {
    img: require("@/assets/images/circle_Barringtoniaasiatica.png"),
    name: { zh: "棋盤腳", en: "Sea Poison Tree", bn: "バリントニア" },
    scientificName: "Barringtonia asiatica",
    photos: loadSpotPhotos("Barringtoniaasiatica"),
  },
  Taiwancrapemyrtle: {
    img: require("@/assets/images/circle_Taiwancrapemyrtle.png"),
    name: { zh: "九芎", en: "Taiwan Crape Myrtle", bn: "シマサルスベリ" },
    scientificName: "Lagerstroemia subcostata",
    photos: loadSpotPhotos("Taiwancrapemyrtle"),
  },
  Soapberry: {
    img: require("@/assets/images/circle_Soapberry.png"),
    name: { zh: "無患子", en: "Soapberry", bn: "ムクロジ" },
    scientificName: "Sapindus mukorossi",
    photos: loadSpotPhotos("Soapberry"),
  },
  LargeLeavedNanmu: {
    img: require("@/assets/images/circle_LargeLeavedNanmu.png"),
    name: { zh: "大葉楠", en: "Large-leaved Nanmu", bn: "オオバタブ" },
    scientificName: "Machilus japonica var. kusanoi",
    photos: loadSpotPhotos("LargeLeavedNanmu"),
  },
  Erythrina: {
    img: require("@/assets/images/circle_Erythrina.png"),
    name: { zh: "刺桐", en: "Coral Tree", bn: "デイゴ" },
    scientificName: "Erythrina variegata L.",
    photos: loadSpotPhotos("Erythrina"),
  },
  Imperata: {
    img: require("@/assets/images/circle_Imperata.png"),
    name: { zh: "白茅", en: "Sugarcane", bn: "サトウキビ" },
    scientificName: "Imperata officinarum",
    photos: loadSpotPhotos("Imperata"),
  },
  // Ficus: { img: require('@/assets/images/circle_Ficus.png'), name: { zh: '雀榕', en: 'Ficus', bn: 'アコウ' }, scientificName: 'Ficus subpisocarpa Gagnep.', photos: loadSpotPhotos('Ficus') },
};

// 各景點對應的 AR 小遊戲路由（無對應項目的景點不顯示「小遊戲」按鈕）
const SPOT_GAME_ROUTES = {
  Mango: "mangogame",
  Chinaberry: "chinaberrygame",
  Soapberry: "soapberrygame",
};

const AR_SPOTS = [
  { id: "VitexNigra", ...SPOT_META.VitexNigra, lng: 121.117829, lat: 22.792217 },
  { id: "Mango", ...SPOT_META.Mango, lng: 121.117226, lat: 22.793783 },
  { id: "PaperMulberry", ...SPOT_META.PaperMulberry, lng: 121.117318, lat: 22.794435 },
  { id: "SpinyBamboo", ...SPOT_META.SpinyBamboo, lng: 121.117066, lat: 22.79438 },
  { id: "Chinaberry", ...SPOT_META.Chinaberry, lng: 121.117343, lat: 22.793979 },
  { id: "Areca", ...SPOT_META.Areca, lng: 121.117674, lat: 22.794705 },
  {
    id: "Parasolleaftree",
    ...SPOT_META.Parasolleaftree,
    lng: 121.117324,
    lat: 22.794223,
  },
  {
    id: "TaiwanFlamegold",
    ...SPOT_META.TaiwanFlamegold,
    lng: 121.116182,
    lat: 22.792757,
  },
  {
    id: "Barringtoniaasiatica",
    ...SPOT_META.Barringtoniaasiatica,
    lng: 121.115775,
    lat: 22.792275,
  },
  {
    id: "Taiwancrapemyrtle",
    ...SPOT_META.Taiwancrapemyrtle,
    lng: 121.117028,
    lat: 22.794168,
  },
  { id: "Soapberry", ...SPOT_META.Soapberry, lng: 121.116577, lat: 22.793306 },
  {
    id: "LargeLeavedNanmu",
    ...SPOT_META.LargeLeavedNanmu,
    lng: 121.117385,
    lat: 22.794335,
  },
  // { id: 'Erythrina', ...SPOT_META.Erythrina, lng: 121.116447, lat: 22.793114 },
  // { id: 'Imperata', ...SPOT_META.Imperata, lng: 121.117851, lat: 22.794243 },
  // { id: 'Ficus', ...SPOT_META.Ficus, lng: 121.116768, lat: 22.792950 },
];

const STATIC_MARKERS = [
  { id: "s1", label: "考古工作室", side: "right", lng: 121.118256, lat: 22.792321 },
  { id: "s2", label: "考古現場", side: "right", lng: 121.119531, lat: 22.791645 },
  { id: "s3", label: "遊客服務中心", side: "left", lng: 121.1155562, lat: 22.79217 },
  { id: "s4", label: "戶外表演廣場", side: "left", lng: 121.115798, lat: 22.792743 },
  { id: "s6", label: "地景藝術", side: "right", lng: 121.117152, lat: 22.793479 },
  { id: "s7", label: "展示廳", side: "left", lng: 121.116328, lat: 22.793814 },
  { id: "s8", label: "西側瞭望台", side: "left", lng: 121.115421, lat: 22.793986 },
  { id: "s9", label: "自然教室", side: "left", lng: 121.117197, lat: 22.794911 },
];

export default {
  name: "MAPpage",
  components: {
    TopNavigation,
  },
  data() {
    return {
      isShowIndexBtn: true,
      isShowMapBtn: false,
      isShowDataBtn: false,
      isShowHint: false,
      currentZoom: MIN_ZOOM,
      indexLink: window.location.protocol + "//" + window.location.host + getBasePath(),
      locationStatus: null,
      selectedSpot: null,
      spotPhotoIndex: 0,
      devMode: false,
      testGps: false,
      isSpotOutOfRange: false,
      arSpots: AR_SPOTS,
      languageTexts: {
        zh: {
          hintTitle: "體驗流程",
          hintText: '請<span class="highlightRed">點選對應的圖案</span>',
          hintButton: "關閉",
          locating: "定位中…",
          locateFailed: "無法取得位置",
          locateDenied: "位置權限被拒絕",
          outOfBounds: "您目前不在活動範圍內，請前往指定地點使用本地圖。",
          scientificLabel: "學名",
          arButton: "AR 體驗",
          gameButton: "小遊戲",
        },
        en: {
          hintTitle: "Experience Flow",
          hintText:
            'Please <span class="highlightRed">tap the corresponding icons</span>',
          hintButton: "Close",
          locating: "Locating…",
          locateFailed: "Unable to get location",
          locateDenied: "Location permission denied",
          outOfBounds:
            "You are currently outside the event area. Please go to the designated location to use this map.",
          scientificLabel: "Scientific Name",
          arButton: "AR Experience",
          gameButton: "Mini Game",
        },
        bn: {
          hintTitle: "体験の流れ",
          hintText:
            '現地の<span class="highlightRed">対応するアイコンをタップしてください</span>',
          hintButton: "閉じる",
          locating: "位置取得中…",
          locateFailed: "位置を取得できません",
          locateDenied: "位置のアクセスが拒否されました",
          outOfBounds:
            "現在、イベントエリア外にいます。指定された場所へ移動してからご利用ください。",
          scientificLabel: "学名",
          arButton: "AR 体験",
          gameButton: "ミニゲーム",
        },
      },
    };
  },
  computed: {
    currentLanguage() {
      return Cookies.get("lang") || "zh";
    },
  },
  async mounted() {
    if (DEV_MODE) {
      this.devMode = true;
    }

    this.locationStatus = this.languageTexts[this.currentLanguage].locating;

    this.initMap();
    // 提示燈箱只出現一次，關閉紀錄在回到首頁時清除
    this.isShowHint = !hasSeenMapHint();
    this.startCompassTracking();

    // 手機重新整理時 MapLibre canvas 可能抓到錯誤尺寸，延遲後強制重算
    setTimeout(() => {
      if (this._map) this._map.resize();
    }, 300);

    // iOS 13+ 的方向權限只能在使用者互動的當下請求，原本只掛在提示燈箱的關閉按鈕上
    // （closeHintBox），但提示燈箱現在只出現一次——從 AR 頁「返回地圖」是整頁重新載入，
    // 這次 isShowHint 會是 false（已讀過），燈箱不出現，closeHintBox 永遠不會觸發，
    // 導致方向權限請求整個被跳過。改成監聽這個頁面「第一次任意點擊」都會觸發一次，
    // 不管有沒有出現提示燈箱都能正常要到權限；重複觸發沒關係，
    // requestOrientationPermission/listenToOrientation 本來就有防止重複綁定的判斷。
    this._firstTapHandler = () => {
      this.requestOrientationPermission();
      document.removeEventListener("click", this._firstTapHandler, true);
      this._firstTapHandler = null;
    };
    document.addEventListener("click", this._firstTapHandler, true);
  },
  beforeUnmount() {
    // 停止 GPS 追蹤
    if (this._watchId != null) {
      navigator.geolocation.clearWatch(this._watchId);
      this._watchId = null;
    }
    // 移除「第一次點擊」監聽（若使用者還沒點過就離開了這頁）
    if (this._firstTapHandler) {
      document.removeEventListener("click", this._firstTapHandler, true);
      this._firstTapHandler = null;
    }
    // 移除方向監聽
    if (this._orientationHandler) {
      window.removeEventListener("deviceorientationabsolute", this._orientationHandler);
      window.removeEventListener("deviceorientation", this._orientationHandler);
      this._orientationHandler = null;
    }
    // 移除 MapLibre 實例
    if (this._map) {
      this._map.remove();
      this._map = null;
    }
    // 移除 resize 監聽
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
      this._resizeHandler = null;
    }
    // 停止景點照片自動輪播
    this.stopSpotPhotoAutoplay();
  },
  methods: {
    // 初始化 MapLibre 地圖
    initMap() {
      // 自訂地圖圖片的四角經緯度邊界
      // 順序：左上、右上、右下、左下
      const mapBounds = [
        [BOUNDS.west, BOUNDS.north], // 左上
        [BOUNDS.east, BOUNDS.north], // 右上
        [BOUNDS.east, BOUNDS.south], // 右下
        [BOUNDS.west, BOUNDS.south], // 左下
      ];
      const mapCenter = [
        (BOUNDS.west + BOUNDS.east) / 2,
        (BOUNDS.north + BOUNDS.south) / 2,
      ];

      this._map = new maplibregl.Map({
        container: this.$refs.mapContainer,
        style: {
          version: 8,
          sources: {
            customMap: {
              type: "image",
              url: customMapImg,
              coordinates: mapBounds,
            },
          },
          layers: [
            {
              id: "background",
              type: "background",
              paint: { "background-color": "#e8e8e8" },
            },
            {
              id: "customMap-layer",
              type: "raster",
              source: "customMap",
              paint: { "raster-opacity": 1 },
            },
          ],
        },
        center: mapCenter,
        zoom: 18,
        maxZoom: 19,
        // 限制地圖視野不超出底圖範圍，避免出現空白區域
        maxBounds: [
          [BOUNDS.west, BOUNDS.south], // 西南角
          [BOUNDS.east, BOUNDS.north], // 東北角
        ],
        attributionControl: false,
        dragPan: true,
        dragRotate: true,
      });

      // 縮放以畫面正中央為基準點，限制縮放範圍
      this._map.setMinZoom(MIN_ZOOM);
      this._map.setMaxZoom(MAX_ZOOM);
      this._map.scrollZoom.enable({ around: "center" });
      this._map.touchZoomRotate.enable({ around: "center" });
      this._map.touchZoomRotate.disableRotation();
      this._map.doubleClickZoom.disable();

      // 自訂「回到我的位置」按鈕
      const recenterBtn = document.createElement("button");
      recenterBtn.className = "recenter-btn";
      recenterBtn.title = "回到我的位置";
      recenterBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/></svg>`;
      recenterBtn.addEventListener("click", () => this.recenterMap());
      this.$refs.mapContainer.appendChild(recenterBtn);

      // 建立單一 label 元素，用 project() 定位，不影響 marker anchor
      this._spotLabel = document.createElement("div");
      this._spotLabel.className = "spot-float-label";
      this._spotLabel.style.display = "none";
      this.$refs.mapContainer.appendChild(this._spotLabel);

      this._map.on("load", () => {
        this._map.resize(); // 確保 canvas 尺寸正確
        this.addSpotMarkers();
        this.addStaticMarkers();
        this.startLocationTracking();
      });

      this._map.on("move", () => this._updateSpotLabel());
      this._map.on("zoom", () => this._updateSpotLabel());

      // 追蹤目前縮放值，用於 +/- 按鈕啟用判斷
      this._map.on("zoom", () => {
        this.currentZoom = this._map.getZoom();
      });

      // 視窗大小變化時（含手機轉向）重新計算 canvas 尺寸
      this._resizeHandler = () => {
        if (this._map) this._map.resize();
      };
      window.addEventListener("resize", this._resizeHandler);
    },

    // 在地圖上置入 AR 景點標記
    addStaticMarkers() {
      STATIC_MARKERS.forEach((marker) => {
        const el = document.createElement("div");
        el.className = `static-marker static-marker--${marker.side}`;
        el.innerHTML = `
          <span class="static-marker__label">${marker.label}</span>
          <div class="static-marker__dot"></div>
        `;
        new maplibregl.Marker({
          element: el,
          anchor: marker.side === "left" ? "right" : "left",
        })
          .setLngLat([marker.lng, marker.lat])
          .addTo(this._map);
      });
    },
    addSpotMarkers() {
      this._spotMarkerEls = [];
      this.arSpots.forEach((spot) => {
        const el = document.createElement("img");
        el.src = spot.img;
        el.className = "map-spot-marker";
        el.title = spot.id;
        el.addEventListener("click", () => this.openSpotLightbox(spot));

        this._spotMarkerEls.push({ spot, el });

        new maplibregl.Marker({ element: el, anchor: "center" })
          .setLngLat([spot.lng, spot.lat])
          .addTo(this._map);
      });
    },

    // 開啟 GPS 持續追蹤
    startLocationTracking() {
      if (this.testGps) {
        this.locationStatus = null;
        this.updateUserMarker(TEST_LNG, TEST_LAT);
        this._map.flyTo({ center: [TEST_LNG, TEST_LAT], zoom: MIN_ZOOM, duration: 1200 });
        return;
      }
      if (!navigator.geolocation) {
        this.locationStatus = this.languageTexts[this.currentLanguage].locateFailed;
        return;
      }

      this.locationStatus = this.languageTexts[this.currentLanguage].locating;
      this._firstFix = true;
      this._outOfBoundsAlerted = false;

      this._watchId = navigator.geolocation.watchPosition(
        (pos) => {
          const longitude = pos.coords.longitude;
          const latitude = pos.coords.latitude;
          this.locationStatus = null;
          this.updateUserMarker(longitude, latitude);

          // 範圍檢查
          const inBounds =
            longitude >= BOUNDS.west &&
            longitude <= BOUNDS.east &&
            latitude >= BOUNDS.south &&
            latitude <= BOUNDS.north;
          if (!inBounds && !this._outOfBoundsAlerted) {
            this._outOfBoundsAlerted = true;
            alert(this.languageTexts[this.currentLanguage].outOfBounds);
          } else if (inBounds) {
            this._outOfBoundsAlerted = false;
          }

          if (this._firstFix) {
            // 第一次定位：飛移到用戶位置（保持目前縮放階段）
            this._firstFix = false;
            this._map.flyTo({
              center: [longitude, latitude],
              zoom: MIN_ZOOM,
              duration: 1200,
            });
          } else if (!this._gpsPaused) {
            this._map.easeTo({ center: [longitude, latitude], duration: 800 });
          }
        },
        (err) => {
          console.warn("[GPS] error:", err.code, err.message);
          if (err.code === 1) {
            // PERMISSION_DENIED
            this.locationStatus = this.languageTexts[this.currentLanguage].locateDenied;
          } else {
            this.locationStatus = this.languageTexts[this.currentLanguage].locateFailed;
          }
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
      );
    },

    // 切換「測試座標」與「真實 GPS」
    toggleGpsMode() {
      if (this._watchId != null) {
        navigator.geolocation.clearWatch(this._watchId);
        this._watchId = null;
      }
      this.testGps = !this.testGps;
      this.startLocationTracking();
    },

    // 將地圖平滑移回用戶目前位置
    recenterMap() {
      if (this._currentLng != null && this._currentLat != null) {
        this._map.easeTo({ center: [this._currentLng, this._currentLat], duration: 600 });
      }
    },

    // 更新內建用戶裝點
    updateUserMarker(lng, lat) {
      this._currentLng = lng;
      this._currentLat = lat;
      this.checkNearbySpots(lng, lat);
      if (this._userMarker) {
        this._userMarker.setLngLat([lng, lat]);
      } else {
        // 最外層 container 會被 maplibregl.Marker 直接當作它的 _element，
        // 每次 map move/render 都會覆寫它的 transform 做定位平移，
        // 所以旋轉絕對不能加在 container 上，否則會跟地圖定位互相覆蓋打架。
        // 改在中間再包一層 rotateGroup 專門負責旋轉，兩層圖示都放進去。
        const container = document.createElement("div");
        Object.assign(container.style, {
          position: "relative",
          width: "60px",
          height: "60px",
        });

        const rotateGroup = document.createElement("div");
        Object.assign(rotateGroup.style, {
          position: "absolute",
          inset: "0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "transform 0.3s ease-out",
        });

        // 用戶位置圖示
        const userImg = document.createElement("img");
        userImg.src = mapManImg;
        Object.assign(userImg.style, {
          width: "30px",
          height: "30px",
          position: "absolute",
          zIndex: "10000",
          top: "35%",
        });
        // 方向指示器
        const directionImg = document.createElement("img");
        directionImg.src = mapDirectionImg;
        Object.assign(directionImg.style, {
          width: "60px",
          height: "60px",
          position: "absolute",
          zIndex: "9999",
          top: "0",
          left: "0",
        });
        // 旋轉套用在 rotateGroup 上，讓兩張圖示一起轉
        this._directionMarkerEl = rotateGroup;

        rotateGroup.appendChild(userImg);
        rotateGroup.appendChild(directionImg);
        container.appendChild(rotateGroup);

        this._userMarker = new maplibregl.Marker({ element: container, anchor: "center" })
          .setLngLat([lng, lat])
          .addTo(this._map);
      }
    },

    // 啟動設備方向追蹤
    startCompassTracking() {
      if (!window.DeviceOrientationEvent) {
        console.warn("[Compass] DeviceOrientation API not supported");
        return;
      }

      // 對於 iOS 13+ 需要請求權限；對於其他設備，直接開始監聽
      if (typeof DeviceOrientationEvent.requestPermission !== "function") {
        // 非 iOS 或舊版 iOS 直接監聽
        this.listenToOrientation();
        return;
      }

      // iOS：先直接呼叫看看。如果使用者在同一個瀏覽器分頁情境（同一個 session）裡
      // 已經同意過（例如從 AR 頁用 SPA 導航回來，整個頁面情境沒有中斷過），
      // requestPermission() 會直接回傳已經同意的結果，不需要真人手勢就能成功。
      // 只有在權限還沒決定過時才「需要」真人手勢，那種情況這裡會拿不到結果，
      // 之後靠 closeHintBox／_firstTapHandler 這兩個掛在點擊事件上的呼叫來補（見下方），
      // 兩邊都呼叫也沒關係，listenToOrientation 本來就有防止重複綁定的判斷。
      this.requestOrientationPermission();
    },

    // 監聽設備方向事件
    listenToOrientation() {
      // 避免重複添加監聽器
      if (this._orientationHandler) {
        return;
      }

      // 初始化上次的角度
      this._lastHeading = 0;

      this._orientationHandler = (event) => {
        let heading = null;

        if (event.webkitCompassHeading !== undefined) {
          // iOS Safari
          heading = event.webkitCompassHeading;
        } else if (event.alpha !== null && event.absolute) {
          // Android Chrome (deviceorientationabsolute)
          heading = 360 - event.alpha;
        } else if (event.alpha !== null) {
          // 一般 deviceorientation
          heading = 360 - event.alpha;
        }

        if (heading !== null && this._directionMarkerEl) {
          // 計算最短旋轉路徑，避免跨越 0/360 度時轉一整圈
          let delta = heading - this._lastHeading;

          // 如果差值超過 180 度，走反方向更短
          if (delta > 180) {
            delta -= 360;
          } else if (delta < -180) {
            delta += 360;
          }

          // 累加到當前角度（不受 0-360 限制）
          this._lastHeading += delta;

          this._directionMarkerEl.style.transform = `rotate(${this._lastHeading}deg)`;
        }
      };

      // 優先使用 deviceorientationabsolute（提供絕對方向）
      // Android Chrome 會同時觸發兩種事件，若已收到 absolute 就不再加 deviceorientation 避免重複套用
      this._hasAbsoluteOrientation = false;
      window.addEventListener(
        "deviceorientationabsolute",
        (e) => {
          this._hasAbsoluteOrientation = true;
          this._orientationHandler(e);
        },
        true
      );
      setTimeout(() => {
        if (!this._hasAbsoluteOrientation) {
          window.addEventListener("deviceorientation", this._orientationHandler, true);
        }
      }, 1000);
    },

    // Haversine 公式：計算兩個 GPS 座標間的距離（公尺）
    calcDistance(lat1, lon1, lat2, lon2) {
      const R = 6371000;
      const φ1 = (lat1 * Math.PI) / 180;
      const φ2 = (lat2 * Math.PI) / 180;
      const Δφ = ((lat2 - lat1) * Math.PI) / 180;
      const Δλ = ((lon2 - lon1) * Math.PI) / 180;
      const a =
        Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    },

    // 檢查並更新各點位與用戶距離，5 公尺以內切換 nearby 樣式
    checkNearbySpots(lng, lat) {
      if (!this._spotMarkerEls) return;
      this._spotMarkerEls.forEach(({ spot, el }) => {
        const dist = this.calcDistance(lat, lng, spot.lat, spot.lng);
        el.classList.toggle("nearby", dist <= 10000);
      });
      // GPS 持續更新位置時，同步重新檢查目前開啟的景點是否還在 AR 可用範圍內
      if (this.selectedSpot) {
        const dist = this.calcDistance(
          lat,
          lng,
          this.selectedSpot.lat,
          this.selectedSpot.lng
        );
        this.isSpotOutOfRange = dist > AR_TRIGGER_DISTANCE;
      }
    },

    // 請求設備方向權限（iOS）
    async requestOrientationPermission() {
      if (
        typeof DeviceOrientationEvent !== "undefined" &&
        typeof DeviceOrientationEvent.requestPermission === "function"
      ) {
        try {
          const permission = await DeviceOrientationEvent.requestPermission();
          if (permission === "granted") {
            this.listenToOrientation();
          }
        } catch (error) {
          console.error("[Compass] Permission denied:", error);
        }
      }
    },

    _updateSpotLabel() {
      if (!this._spotLabel || !this._selectedMarkerInfo) return;
      const { spot } = this._selectedMarkerInfo;
      const pos = this._map.project([spot.lng, spot.lat]);
      this._spotLabel.style.left = `${pos.x}px`;
      this._spotLabel.style.top = `${pos.y}px`;
    },
    openSpotLightbox(spot) {
      if (this._selectedMarkerInfo) {
        const { spot: prev, el } = this._selectedMarkerInfo;
        el.src = prev.img;
        el.classList.remove("spot-selected");
        this._selectedMarkerInfo = null;
      }
      const info = this._spotMarkerEls.find((m) => m.spot.id === spot.id);
      if (info) {
        try {
          info.el.src = require(`@/assets/images/circle_${spot.id}_pressed.png`);
        } catch {
          // pressed 圖不存在時保持原圖
        }
        info.el.classList.add("spot-selected");
        this._selectedMarkerInfo = { spot, el: info.el };
      }
      if (this._spotLabel) {
        this._spotLabel.textContent = spot.name.zh;
        this._spotLabel.style.display = "block";
        this._updateSpotLabel();
      }
      this._gpsPaused = true;
      this._map.easeTo({ center: [spot.lng, spot.lat], duration: 600 });
      this.spotPhotoIndex = 0;
      this.selectedSpot = spot;
      // 開啟當下立刻算一次距離，不用等下一次 GPS 更新才知道 AR 按鈕能不能點；
      // 定位還沒拿到座標時保守地視為超出範圍
      this.isSpotOutOfRange =
        this._currentLng == null || this._currentLat == null
          ? true
          : this.calcDistance(this._currentLat, this._currentLng, spot.lat, spot.lng) >
            AR_TRIGGER_DISTANCE;
      this.startSpotPhotoAutoplay();
    },
    startSpotPhotoAutoplay() {
      this.stopSpotPhotoAutoplay();
      const len =
        this.selectedSpot && this.selectedSpot.photos
          ? this.selectedSpot.photos.length
          : 0;
      if (len < 2) return;
      this._spotPhotoTimer = setInterval(() => {
        this.spotPhotoIndex = (this.spotPhotoIndex + 1) % len;
      }, 3000);
    },
    stopSpotPhotoAutoplay() {
      if (this._spotPhotoTimer != null) {
        clearInterval(this._spotPhotoTimer);
        this._spotPhotoTimer = null;
      }
    },
    closeLightbox() {
      if (this._selectedMarkerInfo) {
        const { spot, el } = this._selectedMarkerInfo;
        el.src = spot.img;
        el.classList.remove("spot-selected");
        this._selectedMarkerInfo = null;
      }
      if (this._spotLabel) this._spotLabel.style.display = "none";
      this.selectedSpot = null;
      this._gpsPaused = false;
      this.stopSpotPhotoAutoplay();
      if (this._currentLng != null && this._currentLat != null) {
        this._map.easeTo({ center: [this._currentLng, this._currentLat], duration: 600 });
      }
    },
    goToAR(circleId) {
      window.location.href = this.indexLink + "ar?point=" + circleId;
    },
    spotGameRoute(circleId) {
      return SPOT_GAME_ROUTES[circleId];
    },
    goToGame(circleId) {
      const route = this.spotGameRoute(circleId);
      if (!route) return;
      window.location.href = this.indexLink + route + "?point=" + circleId;
    },
    getSpotIcon(id) {
      try {
        return require(`@/assets/images/icon_${id}.svg`);
      } catch {
        return null;
      }
    },
    onBackToIndex() {
      window.location.href = this.indexLink;
    },
    closeHintBox() {
      this.isShowHint = false;
      markMapHintSeen();
      // iOS 需要在用戶交互時請求方向權限
      this.requestOrientationPermission();
    },
    zoomIn() {
      const next = Math.min(this._map.getZoom() + ZOOM_STEP, MAX_ZOOM);
      this._map.easeTo({ zoom: next, duration: 400 });
    },
    zoomOut() {
      const next = Math.max(this._map.getZoom() - ZOOM_STEP, MIN_ZOOM);
      this._map.easeTo({ zoom: next, duration: 400 });
    },
  },
};
</script>
<style>
@font-face {
  font-family: "MyFont";
  src: url("@/assets/fonts/700.otf") format("opentype");
  font-weight: bold;
  font-style: normal;
  font-display: swap;
}
</style>
<style scoped>
body {
  height: 100vh !important;
  overflow: hidden !important;
}

.recenter-btn {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 10;
  width: 40px;
  height: 40px;
  background: white;
  border: none;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
  padding: 0;
}

.recenter-btn:active {
  background: #f0f0f0;
}

.zoom-controls {
  position: absolute;
  right: 10px;
  bottom: 55px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.zoom-btn {
  width: 40px;
  height: 40px;
  background: white;
  border: none;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  font-size: 22px;
  line-height: 1;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.zoom-btn:active {
  background: #f0f0f0;
}

.zoom-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

#main_page {
  height: 100svh;
  overflow: hidden;
  position: relative;
}

.en {
  letter-spacing: 0px !important;
}

/* 地圖容器 */
.maplibre-container {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.gps-toggle-btn {
  position: fixed;
  top: 16px;
  left: 16px;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 12px;
  padding: 6px 12px;
  border: none;
  border-radius: 20px;
  cursor: pointer;
}

.location-status {
  position: absolute;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.65);
  color: white;
  font-size: 13px;
  padding: 5px 14px;
  border-radius: 20px;
  z-index: 100;
  pointer-events: none;
  white-space: nowrap;
}

/* 靜態點位 */
:deep(.static-marker) {
  display: flex;
  align-items: center;
  gap: 5px;
  pointer-events: none;
}
:deep(.static-marker--right) {
  flex-direction: row-reverse;
}
:deep(.static-marker--left) {
  flex-direction: row;
}
:deep(.static-marker__dot) {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(76, 36, 4, 1);
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px rgba(76, 36, 4, 1);
  flex-shrink: 0;
}
:deep(.static-marker__label) {
  font-size: 11px;
  font-weight: bold;
  color: rgba(76, 36, 4, 1);
  white-space: nowrap;
  -webkit-text-stroke: 2px #fff;
  paint-order: stroke fill;
}

/* 選中 spot 的浮動名稱 label（獨立於 marker element，不影響 anchor） */
:deep(.spot-float-label) {
  position: absolute;
  transform: translate(-50%, calc(-100% - 40px));
  background: rgba(5, 30, 30, 1);
  color: #fff;
  font-size: 13px;
  font-weight: bold;
  padding: 5px 14px;
  border-radius: 20px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 100000;
}

:deep(.spot-float-label::after) {
  content: "";
  position: absolute;
  bottom: -7px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 8px 6px 0 6px;
  border-style: solid;
  border-color: rgba(5, 30, 30, 1) transparent transparent transparent;
}

/* AR 景點標記 */
:deep(.map-spot-marker) {
  width: 44px;
  height: 44px;
  object-fit: contain;
  z-index: 100000;
  cursor: pointer;
}

:deep(.map-spot-marker:hover) {
  transform: scale(1.15);
}

:deep(.map-spot-marker.spot-selected) {
  width: 65px;
  height: 65px;
}

/* hint-lightbox */
.hint-lightbox {
  background-color: rgba(60, 60, 60, 0.5);
  position: fixed;
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.hint-content {
  background: url(@/assets/images/map_lightbox_bg.png) no-repeat center center;
  background-size: cover;
  width: 76%;
  max-width: 800px;
  padding: 5%;
  box-shadow: 0px 0px 15px 10px rgba(61, 61, 61, 0.3),
    0px 0px 0px 15px rgba(255, 255, 255, 0.5);
  border-radius: 5px;
  position: relative;
}

.hint-lightbox .hint-button {
  position: absolute;
  background: url(@/assets/images/btn_background.png) no-repeat center center;
  background-size: 120% auto;
  color: white;
  font-family: "MyFont", sans-serif;
  padding: 1% 8%;
  border-radius: 5px;
  cursor: pointer;
  transform: translate(-50%, 30%);
  left: 50%;
  font-size: 24px;
  letter-spacing: 2px;
  border-radius: 25px;
}

.hint-title {
  color: white;
  background-color: #436ab2;
  width: fit-content;
  margin: auto;
  font-family: "MyFont", sans-serif;
  padding: 2% 10%;
  border-radius: 5px;
  letter-spacing: 3px;
  font-size: 20px;
}

.hint-box {
  display: flex;
  justify-content: center;
  width: 85%;
  gap: 10%;
  margin: 5% auto;
}

.hint-box .topic {
  font-family: "MyFont", sans-serif;
  text-align: center;
  margin-bottom: 5px;
  font-size: 18px;
  color: black;
}

.hint-content p {
  font-size: 18px;
}

.hint-content :deep(.highlightRed) {
  font-family: "MyFont", sans-serif;
  color: #c1272d;
}

/* RWD */
@media (max-width: 480px) {
  .hint-content {
    width: 78%;
    padding: 6% 5%;
  }

  .hint-title {
    font-size: 20px;
    padding: 3% 10%;
    letter-spacing: 2px;
  }

  .hint-box {
    width: 90%;
    gap: 8%;
    margin: 4% auto;
  }

  .hint-box .topic {
    font-size: 18px;
  }

  .hint-content p {
    font-size: 18px;
    text-align: center;
  }

  .hint-lightbox .hint-button {
    font-size: 20px;
    padding: 2% 10%;
    letter-spacing: 1px;
  }

  :deep(.map-spot-marker) {
    width: 36px;
    height: 36px;
    object-fit: contain;
  }

  :deep(.map-spot-marker.spot-selected) {
    width: 55px;
    height: 55px;
  }
}

@media (min-width: 481px) and (max-width: 768px) {
  .hint-content {
    width: 72%;
    padding: 5% 5%;
  }

  .hint-title {
    font-size: 22px;
    padding: 2% 9%;
  }

  .hint-box {
    width: 85%;
    gap: 10%;
  }

  .hint-box .topic {
    font-size: 20px;
  }

  .hint-content p {
    font-size: 20px;
    text-align: center;
  }

  .hint-lightbox .hint-button {
    font-size: 22px;
  }
}

@media (orientation: landscape) and (max-height: 500px) {
  .hint-lightbox {
    align-items: flex-start;
    padding-top: 10px;
    overflow-y: auto;
  }

  .hint-content {
    width: 70%;
    padding: 3% 4%;
    margin-bottom: 60px;
  }

  .hint-title {
    font-size: 14px;
    padding: 1% 6%;
  }

  .hint-box {
    width: 80%;
    gap: 8%;
    margin: 3% auto;
  }

  .hint-box .topic {
    font-size: 12px;
  }

  .hint-content p {
    font-size: 13px;
    text-align: center;
  }

  .hint-lightbox .hint-button {
    font-size: 15px;
    padding: 1% 7%;
    transform: translate(-50%, 20%);
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .hint-content {
    width: 70%;
  }

  .hint-title {
    font-size: 24px;
  }

  .hint-box .topic {
    font-size: 21px;
  }

  .hint-content p {
    font-size: 21px;
  }

  .hint-lightbox .hint-button {
    font-size: 24px;
  }

  :deep(.map-spot-marker) {
    width: 52px;
    height: 52px;
    object-fit: contain;
  }

  :deep(.map-spot-marker.spot-selected) {
    width: 75px;
    height: 75px;
  }
}

@media (min-width: 1025px) and (max-width: 1366px) {
  .hint-content {
    width: 60%;
    max-width: 700px;
    padding: 4% 5%;
  }

  .hint-title {
    font-size: 28px;
    padding: 2% 10%;
  }

  .hint-box {
    width: 80%;
    gap: 10%;
    margin: 4% auto;
  }

  .hint-box .topic {
    font-size: 24px;
  }

  .hint-content p {
    font-size: 24px;
    text-align: center;
  }

  .hint-lightbox .hint-button {
    font-size: 26px;
  }

  :deep(.map-spot-marker) {
    width: 60px;
    height: 60px;
    object-fit: contain;
  }

  :deep(.map-spot-marker.spot-selected) {
    width: 85px;
    height: 85px;
  }
}

/* ── 景點 Lightbox ─────────────────────────────────────── */
.spot-overlay {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 500;
  display: flex;
  justify-content: center;
  padding-bottom: 30px;
  pointer-events: none;
}

.spot-overlay .spot-card {
  pointer-events: auto;
}

.spot-card {
  background: rgba(255, 255, 255, 0.45);
  border-radius: 28px;
  width: 88%;
  max-width: 480px;
  margin: 0 auto;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  padding: 3%;
  backdrop-filter: blur(10px);
  border: solid 1px white;
}

.spot-photo-area {
  position: relative;
  width: 100%;
  height: 23vh;
  overflow: hidden;
}

.spot-photo-track {
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.3s ease;
}

.spot-photo {
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 19px;
}

.spot-photo-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #a8d880 0%, #6dac3c 100%);
}

.spot-photo-dots {
  position: absolute;
  bottom: 6%;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
}

.spot-photo-dot {
  width: 6px;
  height: 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.6);
  transition: width 0.2s ease, background 0.2s ease;
}

.spot-photo-dot.active {
  width: 16px;
  background: #fff;
}

.spot-close-btn {
  position: absolute;
  top: 5%;
  right: 5%;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.45);
  backdrop-filter: blur(10px);
  border: none;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(17, 33, 1, 1);
}

.spot-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2% 0px 0px 4%;
  gap: 12px;
}

.spot-info-left {
  flex: 1;
  min-width: 0;
}

.spot-name-row {
  display: flex;
  align-items: center;
  gap: 1%;
}

.spot-name {
  font-size: 22px;
  font-weight: bold;
  font-family: "MyFont", sans-serif;
  color: #1a1a1a;
}

.spot-name-icon {
  width: 10vw;
  height: 10vw;
  flex-shrink: 0;
}

.spot-scientific {
  font-size: 13px;
  color: rgba(17, 33, 1, 1);
  margin-top: 4px;
  text-align: left;
}

.spot-btn-group {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.spot-ar-btn,
.spot-game-btn {
  flex-shrink: 0;
  background: linear-gradient(180deg, #668c42 0%, #477a17 100%);
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 10px 15px;
  font-size: 15px;
  font-family: "MyFont", sans-serif;
  letter-spacing: 1px;
  cursor: pointer;
  white-space: nowrap;
}

.spot-game-btn {
  background: linear-gradient(180deg, #f2b441 0%, #d97706 100%);
}

.spot-ar-btn:active {
  background: linear-gradient(180deg, #668c42 0%, #477a17 100%),
    linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2));
}

.spot-game-btn:active {
  background: linear-gradient(180deg, #f2b441 0%, #d97706 100%),
    linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2));
}

.spot-ar-btn:disabled {
  background: #9a9a9a;
  cursor: not-allowed;
}

.spot-out-of-range-hint {
  font-size: 11px;
  color: #b3261e;
  text-align: right;
  margin-top: 4px;
  max-width: 140px;
}

/* slide-up transition */
.spot-slide-enter-active,
.spot-slide-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.spot-slide-enter-from,
.spot-slide-leave-to {
  opacity: 0;
  transform: translateY(40px);
}
</style>
